import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "../contexts/AdminAuthContext";
import { Select, Spinner, Button, HStack, Image, Tooltip } from "@chakra-ui/react";
import { ChatIcon, AttachmentIcon } from "@chakra-ui/icons";
import styled from "styled-components";
import { getClients, Client, Campaign, getCampaignsByClient, CampaignListItem, getCampaign } from "../api/admin/adminCampaign";
import { ChatInterface } from "../components/admin/ChatInterface";
import { CampaignDetailsPanel } from "../components/admin/CampaignDetailsPanel";
import { useViewport } from "../hooks/useViewport";
import skipgiveLogo from "../assets/skipgive-icon.png";
import { UploadContactsModal } from "../components/dashboard/modals/UploadContactsModal";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background: #f8fafc;
`;

const ContentWrapper = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: ${(props) => (props.$isMobile ? "column" : "row")};
  flex: 1;
  overflow: hidden;
`;

const ChatSection = styled.div<{ $isMobile: boolean; $showDetails: boolean; $isEditingCampaign: boolean; $showAiChat: boolean }>`
  width: ${(props) => {
    if (props.$isMobile) return "100%";
    if (props.$isEditingCampaign && !props.$showAiChat) return "0"; // Hidden when editing and chat off
    if (props.$isEditingCampaign) return "50%"; // Equal split when editing with chat visible
    return props.$showDetails ? "60%" : "100%"; // Normal when creating new campaign
  }};
  height: ${(props) => {
    if (!props.$isMobile) return "100%";
    // On mobile: hide chat section when editing existing campaign
    if (props.$isEditingCampaign) return "0";
    // On mobile: show chat for new campaigns
    return "100%";
  }};
  display: ${(props) => {
    if (props.$isMobile && props.$isEditingCampaign) return "none";
    if (!props.$isMobile && props.$isEditingCampaign && !props.$showAiChat) return "none";
    return "flex";
  }};
  flex-direction: column;
  border-right: ${(props) => (props.$isMobile ? "none" : "1px solid #e2e8f0")};
  border-bottom: ${(props) => (props.$isMobile ? "1px solid #e2e8f0" : "none")};
  transition: width 0.3s ease-in-out, height 0.3s ease-in-out;
  overflow: hidden;
`;

const DetailsSection = styled.div<{ $isMobile: boolean; $show: boolean; $isEditingCampaign: boolean; $showAiChat: boolean }>`
  width: ${(props) => {
    if (props.$isMobile) return "100%";
    if (props.$isEditingCampaign && !props.$showAiChat) return "100%"; // Full width when chat is hidden
    return props.$isEditingCampaign ? "50%" : "40%"; // Split when chat visible
  }};
  height: ${(props) => {
    if (!props.$isMobile) return "100%";
    // On mobile: full height when editing existing campaign
    if (props.$isEditingCampaign) return "100%";
    // On mobile: hide details when creating new campaign (chat is showing)
    return "0";
  }};
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  background: white;
  transform: translateX(${(props) => (props.$show ? "0" : "100%")});
  opacity: ${(props) => (props.$show ? "1" : "0")};
  display: ${(props) => {
    if (props.$isMobile && !props.$isEditingCampaign) return "none";
    return "block";
  }};
  transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out, width 0.3s ease-in-out, height 0.3s ease-in-out;
  position: ${(props) => (props.$show ? "relative" : "absolute")};
  right: 0;
`;

const Header = styled.div`
  padding: 1rem 1.5rem;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    gap: 0.5rem;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
`;

export const Admin = () => {
  const { adminToken, logout, isLoading: authLoading } = useAdminAuth();
  const navigate = useNavigate();
  const { isMobile } = useViewport();
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [campaigns, setCampaigns] = useState<CampaignListItem[]>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>("");
  const [currentCampaign, setCurrentCampaign] = useState<Campaign | null>(null);
  const [isLoadingClients, setIsLoadingClients] = useState(true);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [isLoadingCampaign, setIsLoadingCampaign] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<any>({});
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [showAiChat, setShowAiChat] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // Restore state from localStorage
  useEffect(() => {
    const savedClientId = localStorage.getItem("admin_selected_client");
    const savedCampaignId = localStorage.getItem("admin_selected_campaign");
    if (savedClientId) setSelectedClientId(savedClientId);
    if (savedCampaignId) setSelectedCampaignId(savedCampaignId);
  }, []);

  // Save state to localStorage
  useEffect(() => {
    if (selectedClientId) {
      localStorage.setItem("admin_selected_client", selectedClientId);
    }
  }, [selectedClientId]);

  useEffect(() => {
    if (selectedCampaignId) {
      localStorage.setItem("admin_selected_campaign", selectedCampaignId);
    }
  }, [selectedCampaignId]);

  useEffect(() => {
    if (!authLoading && !adminToken) {
      navigate("/admin");
    }
  }, [adminToken, authLoading, navigate]);

  const loadClients = useCallback(async () => {
    if (!adminToken) return;
    try {
      const data = await getClients(adminToken);
      setClients(data);
      setIsLoadingClients(false);
    } catch (error) {
      console.error("Failed to load clients:", error);
      setIsLoadingClients(false);
    }
  }, [adminToken]);

  const loadCampaigns = useCallback(async (clientId: string) => {
    if (!adminToken) return;
    setIsLoadingCampaigns(true);
    try {
      const data = await getCampaignsByClient(adminToken, clientId);
      setCampaigns(data);
    } catch (error) {
      console.error("Failed to load campaigns:", error);
    } finally {
      setIsLoadingCampaigns(false);
    }
  }, [adminToken]);

  const loadCampaign = useCallback(async (campaignId: string) => {
    if (!adminToken) return;
    setIsLoadingCampaign(true);
    try {
      const data = await getCampaign(adminToken, campaignId);
      setCurrentCampaign(data);
      setHasUserInteracted(true);
    } catch (error) {
      console.error("Failed to load campaign:", error);
    } finally {
      setIsLoadingCampaign(false);
    }
  }, [adminToken]);

  useEffect(() => {
    if (adminToken) {
      loadClients();
    }
  }, [adminToken, loadClients]);

  useEffect(() => {
    if (selectedClientId) {
      loadCampaigns(selectedClientId);
    }
  }, [selectedClientId, loadCampaigns]);

  useEffect(() => {
    if (selectedCampaignId && selectedCampaignId !== "new") {
      loadCampaign(selectedCampaignId);
      setShowAiChat(false); // Hide AI chat when editing existing campaign
    } else if (selectedCampaignId === "new") {
      setCurrentCampaign(null);
      setHasUserInteracted(false);
      setAiSuggestions({}); // Clear AI suggestions when creating new campaign
      setShowAiChat(true); // Show AI chat for new campaigns
    }
  }, [selectedCampaignId, loadCampaign]);

  if (authLoading || isLoadingClients) {
    return (
      <LoadingContainer>
        <Spinner size="xl" color="#339CFF" />
      </LoadingContainer>
    );
  }

  if (!adminToken) {
    return null;
  }

  const handleCampaignUpdate = (campaign: Campaign | null) => {
    setCurrentCampaign(campaign);
    if (campaign) {
      setHasUserInteracted(true);
      // Update selected campaign ID to match the current campaign
      setSelectedCampaignId(campaign.id);
      // Reload campaigns list to show newly created campaign
      if (selectedClientId) {
        loadCampaigns(selectedClientId);
      }
    } else {
      // Campaign was deleted - reset state
      setSelectedCampaignId("");
      setHasUserInteracted(false);
      localStorage.removeItem("admin_selected_campaign");
      // Reload campaigns list to remove deleted campaign
      if (selectedClientId) {
        loadCampaigns(selectedClientId);
      }
    }
  };

  const handleCsvUploadSuccess = () => {
    // Modal will handle closing itself
  };

  const isEditingCampaign = !!currentCampaign && selectedCampaignId !== "new";

  return (
    <Container>
      <Header>
        <Image src={skipgiveLogo} alt="SkipGive" height="32px" mr={3} />
        <HStack spacing={2} flex={1}>
          <Select
            placeholder="Select client..."
            value={selectedClientId}
            onChange={(e) => {
              setSelectedClientId(e.target.value);
              setSelectedCampaignId("");
              setCurrentCampaign(null);
            }}
            maxWidth={isMobile ? "100%" : "200px"}
            size="md"
            isDisabled={isLoadingCampaigns || isLoadingCampaign}
          >
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.organization}
              </option>
            ))}
          </Select>
          {selectedClientId && (
            <Select
              placeholder={isLoadingCampaigns ? "Loading..." : !selectedCampaignId ? "Select campaign..." : undefined}
              value={selectedCampaignId}
              onChange={(e) => setSelectedCampaignId(e.target.value)}
              maxWidth={isMobile ? "100%" : "200px"}
              size="md"
              isDisabled={isLoadingCampaigns || isLoadingCampaign}
              key={`campaign-select-${campaigns.length}`}
            >
              <option value="new">+ New Campaign</option>
              {campaigns.map((campaign) => {
                const date = new Date(campaign.createdAt);
                const formattedDate = date.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                });
                const displayName = `${campaign.name} (${formattedDate})`;

                return (
                  <option key={campaign.id} value={campaign.id}>
                    {displayName}
                  </option>
                );
              })}
            </Select>
          )}
        </HStack>
        <HStack spacing={2}>
          {selectedClientId && (
            <Tooltip label="Upload contacts CSV for this client">
              <Button
                onClick={() => setIsUploadModalOpen(true)}
                size="sm"
                leftIcon={<AttachmentIcon />}
                colorScheme="green"
                variant="outline"
              >
                Upload CSV
              </Button>
            </Tooltip>
          )}
          {isEditingCampaign && !isMobile && (
            <Tooltip label={showAiChat ? "Hide AI Assistant" : "Show AI Assistant"}>
              <Button
                onClick={() => setShowAiChat(!showAiChat)}
                size="sm"
                leftIcon={<ChatIcon />}
                colorScheme={showAiChat ? "blue" : "gray"}
                variant={showAiChat ? "solid" : "outline"}
              >
                AI Chat
              </Button>
            </Tooltip>
          )}
          <Button onClick={logout} size="sm" colorScheme="red" variant="outline">
            Logout
          </Button>
        </HStack>
      </Header>
      <ContentWrapper $isMobile={isMobile}>
        <ChatSection $isMobile={isMobile} $showDetails={hasUserInteracted || !!currentCampaign} $isEditingCampaign={isEditingCampaign} $showAiChat={showAiChat}>
          <ChatInterface
            selectedClientId={selectedClientId}
            currentCampaign={currentCampaign}
            onCampaignUpdate={handleCampaignUpdate}
            onAiSuggestions={setAiSuggestions}
            onUserInteraction={() => setHasUserInteracted(true)}
          />
        </ChatSection>
        <DetailsSection $isMobile={isMobile} $show={hasUserInteracted || !!currentCampaign} $isEditingCampaign={isEditingCampaign} $showAiChat={showAiChat}>
          <CampaignDetailsPanel
          campaign={currentCampaign}
          selectedClientId={selectedClientId}
          isIndividualClient={clients.find(c => c.id === selectedClientId)?.ein?.startsWith("IND-") ?? false}
          onCampaignUpdate={handleCampaignUpdate}
          aiSuggestions={aiSuggestions}
          onSuggestionsApplied={() => setAiSuggestions({})}
          isLoading={isLoadingCampaign}
        />
      </DetailsSection>
      </ContentWrapper>
      <UploadContactsModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onSuccess={handleCsvUploadSuccess}
        clientId={selectedClientId}
        accessToken={adminToken}
      />
    </Container>
  );
};
